import { useState, useRef, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MapContext from "../../MapContext";
import { Popup } from "@progress/kendo-react-popup";
import { FiMoreVertical } from "react-icons/fi";
import { inAndOut } from "ol/easing";
import { getCenter } from "ol/extent";
import { removeFeature } from "../../../redux/resultsSlice";
import { setFeaturesToBuffer } from "../../../redux/identifySlice";
import {
    setActiveSideBarTab,
    setActiveTool,
    setHorizontalView,
} from "../../../redux/toolSelectSlice";
import { determineSize } from "./Identify/FeatureDescription";
import { editFeature, cloneFeature, clearEdits } from "../../../redux/editingSlice";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DeleteFeatureData } from "../../Editing/editUtils";
import { getMapLayerById } from "../../utils/mapUtils";
const ratio = 50;

const LayerPopup = ({ feature, layerId }) => {
    const [show, setShow] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const popupAnchor = useRef();
    const popupRef = useRef();
    const { map } = useContext(MapContext);
    const activeTool = useSelector((state) => state.toolSelect.tool);
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const horizontalViewOn = useSelector((state) => state.toolSelect.horizontalView);

    const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
    const dispatch = useDispatch();

    const handlePopup = () => {
        setShow(!show);
    };

    const detectClick = (e) => {
        if (
            !popupRef.current.contains(e.target) &&
            !popupAnchor.current.contains(e.target)
        ) {
            setShow(false);
        }
    };

    useEffect(() => {
        if (show) {
            document.addEventListener("mousedown", detectClick);
        }
        return () => {
            document.removeEventListener("mousedown", detectClick);
        };
    }, [show]);

    const handleSwitchView = () => {
        dispatch(setHorizontalView(!horizontalViewOn));
    };

    const handleZoomToFeature = () => {
        const extent = feature.getGeometry().getExtent();
        map.getView().fit(extent, {
            padding: [50, 60, 60, 60],
            duration: 750,
            easing: inAndOut,
            minResolution: 1,
        });
        setShow(false);
    };

    const handlePanToFeature = () => {
        const geometry = feature.getGeometry();
        const extent = geometry.getExtent();
        const view = map.getView();
        if (geometry.getType() === "Point") {
            const zoom = view.getZoom();
            view.animate({
                center: getCenter(extent),
                zoom: zoom < 20 ? 20 : undefined,
            });
        } else {
            view.fit(extent, {
                size: determineSize(map, ratio),
                duration: 750,
                easing: inAndOut,
            });
        }
        setShow(false);
    };

    const handleRemoveFeature = () => {
        dispatch(removeFeature({ featureId: feature.id_, layerId: layerId }));
        setShow(false);
    };

    const handleBufferOptions = () => {
        if (activeTool !== "identify") {
            dispatch(setActiveTool("identify"));
        }
        if (layerId === "ArcGIS Geocoder") {
            dispatch(
                setFeaturesToBuffer([
                    {
                        layerId: layerId,
                        featureIds: [feature.values_.geometry.flatCoordinates],
                    },
                ])
            );
        } else {
            dispatch(
                setFeaturesToBuffer([{ layerId: layerId, featureIds: [feature.id_] }])
            );
        }
        setShow(false);
    };

    const handleEditOptions = () => {
        dispatch(clearEdits());
        dispatch(editFeature({ featureId: feature.id_, layerId: layerId }));
        dispatch(setActiveTool("edit"));
        dispatch(setActiveSideBarTab(1));
        setShow(false);
    };

    const handleCopyFeatureOptions = () => {
        dispatch(cloneFeature({ featureId: feature.id_, layerId: layerId }));
        setShow(false);
    };

    const handleDeleteOptions = () => {
        setShowDeleteConfirmation(true);
        setDeleteErrorMessage();
        setShow(false);
    };
    const closeDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setDeleteErrorMessage();
    };
    const onDeleteData = async () => {
        let lyr = mapConfig.DeleteLayers.find((o) => {
            return o.Id === layerId;
        });
        DeleteFeatureData(feature.id_, lyr, (evt) => {
            if (evt.error) {
                setDeleteErrorMessage(evt.error);
            } else {
                setShowDeleteConfirmation(false);
                setDeleteErrorMessage("");
                // Remove feature from state
                dispatch(removeFeature({ featureId: feature.id_, layerId: layerId }));
                // Refresh layers to unshow removed feature
                const mapLayer = getMapLayerById(map, layerId).getSource();
                mapLayer.refresh();
                const layerLabel = getMapLayerById(map, layerId + "_label").getSource();
                if (layerLabel) {
                    layerLabel.refresh();
                }
            }
        });
    };
    return (
        <>
            {showDeleteConfirmation && !deleteErrorMessage && (
                <Dialog
                    title={"Delete Data"}
                    onClose={closeDeleteConfirmation}
                    width={350}
                    id="delete-data-dialog">
                    <div>Are you sure you want to permanently delete this feature?</div>
                    <DialogActionsBar layout={"stretched"}>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={closeDeleteConfirmation}>
                            Cancel
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={onDeleteData}>
                            Delete
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {showDeleteConfirmation && deleteErrorMessage && (
                <Dialog
                    title={"Error Deleting Data"}
                    onClose={closeDeleteConfirmation}
                    width={350}>
                    <div>{deleteErrorMessage}</div>
                    <DialogActionsBar layout={"stretched"}>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={closeDeleteConfirmation}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <div
                style={{ width: "20px", height: "20px" }}
                onClick={handlePopup}
                ref={popupAnchor}
                className="layer-options-button">
                <FiMoreVertical />
            </div>
            <Popup
                anchor={popupAnchor.current}
                show={show}
                className="layer-options-popup">
                <div ref={popupRef}>
                    {" "}
                    <div className="popup-option" onClick={handleSwitchView}>
                        {horizontalViewOn ? (
                            <>
                                <div className="popup-label">Switch to Side Bar</div>
                                <div>Show feature details in side bar</div>
                            </>
                        ) : (
                            <>
                                <div className="popup-label">
                                    Switch to Horizontal View
                                </div>
                                <div>Show feature details in horizontal view</div>
                            </>
                        )}
                    </div>
                    {
                        <>
                            {mapConfig.EditLayers.find((o) => {
                                return o.Id === layerId;
                            }) && (
                                <div className="popup-option" onClick={handleEditOptions}>
                                    <div className="popup-label">Edit Feature</div>
                                    <div>Edit this feature.</div>
                                </div>
                            )}
                        </>
                    }
                    {
                        <>
                            {mapConfig.DeleteLayers.find((o) => {
                                return o.Id === layerId;
                            }) && (
                                <div
                                    className="popup-option"
                                    onClick={handleDeleteOptions}>
                                    <div className="popup-label">Delete Feature</div>
                                    <div>Delete the current feature</div>
                                </div>
                            )}
                        </>
                    }
                    {
                        <>
                            {mapConfig.EditLayers.find((o) => {
                                return o.Id === layerId;
                            }) &&
                                mapConfig.EditLayers.find((o) => {
                                    return o.Id === layerId;
                                }).CanCopyFeature &&
                                false && (
                                    <div
                                        className="popup-option"
                                        onClick={handleCopyFeatureOptions}>
                                        <div className="popup-label">Copy Feature</div>
                                        <div>Create a copy of this feature</div>
                                    </div>
                                )}
                        </>
                    }
                    <div className="popup-option" onClick={handleZoomToFeature}>
                        {" "}
                        <div className="popup-label">Zoom to Feature</div>
                        <div>Zoom to this feature on the map</div>
                    </div>
                    <div className="popup-option" onClick={handlePanToFeature}>
                        <div className="popup-label">Pan</div>
                        <div>Pan the map to center this feature</div>
                    </div>
                    <div className="popup-option" onClick={handleRemoveFeature}>
                        <div className="popup-label">Remove from Results</div>
                        <div>Remove this feature from the current set of results</div>
                    </div>
                    <div className="popup-option" onClick={handleBufferOptions}>
                        <div className="popup-label">Show Buffer Options</div>
                        <div>Identify features near these results</div>
                    </div>
                </div>
            </Popup>
        </>
    );
};

export default LayerPopup;
